import React, { useEffect, useMemo, useState } from "react";
import './AddOfferModal.scss';
import Modal from 'react-bootstrap/Modal';
import { Button, Spinner } from 'react-bootstrap';
import * as _ from "lodash";
import AddOfferDetails from './AddOfferDetails';
import OfferDetailsConfirmation from './OfferDetailsConfirmation';
import AddOfferModel from "./addOfferModel";
import Offer from "domain/offer";
import { Offering } from "features/settings/offerings/offering";
import PhysicalLocation, { DetermineLaterLocation, defaultPhysicalLocation } from "domain/physicalLocation";
import OfferType, { OfferTypeEnum, toContractType, getOfferTypeName, fromContractType } from "domain/offerType";
import * as OfferActions from "features/offers/offers.actions";
import * as ContractActions from "features/contracts/contracts.actions";
import Commodity from "domain/commodity";
import { DeliveryMethod } from "domain/deliveryMethod";
import DeliveryPeriod from "domain/deliveryPeriod";
import ValidationError from "common/validationError";
import Permissions from "common/security/permissions";
import { PaymentOption, listPaymentOptionsForOffer } from "domain/paymentOption";
import OfferHedgeSettings from './OfferHedgeSettings';
import { OrderType } from "domain/orderType";
import FuturesContract from "domain/futuresContract";
import Contract, { ContractType } from "domain/contract";
import { getPreviousDayForGivenDate } from 'common/formatters';
import { roundCents } from "common/decimal";
import { getCommodityBalanceType } from "domain/commodityBalance";
import * as NP from "number-precision";
import contractService, { ListContractsFilter } from "features/contracts/contractService";
import { useTimer, TimerResult } from 'react-timer-hook';
import moment from "moment";
import { DisplayContractsEnum } from "features/contracts/displayContractsEnum";
import { getFuturesContract as resolveDeliveryPeriodFuturesContract, getSecondLastBusinessDayOfLastMonth } from "./helpers";
import AddCustomFieldModal from "features/customFields/AddCustomFieldModal";
import { BidViewTypesEnum } from "features/bidEditor/bidViewTypesEnum";
import { MerchantFeature, isFeatureEnabled } from "domain/merchant";
import FormValidation, { useFormValidation } from "common/ui/FormValidation";
import offerTypeHelper from "domain/offerTypeHelper";
import useMarketSchedule from "features/systemConfig/useMarketSchedule";
import useDeliveryPeriods from "features/deliveryPeriods/useDeliveryPeriods";
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";
import useTraders from "features/settings/traders/useTraders";
import useFcmAccounts from "features/fcmAccounts/useFcmAccounts";
import useDeliveryMethods from "features/deliveryMethods/useDeliveryMethods";
import { useCustomFieldsLoader } from "features/merchants/useCustomFields";
import usePermission from "common/security/usePermission";
import { useOfferingsLoader } from "features/settings/offerings/useOfferings";
import useStoreSelector from "common/redux/useStoreSelector";
import { useDispatch } from "react-redux";
import { useAllowedDeliveryMethodsLoader } from "features/contracts/useAllowedDeliveryMethods";
import useFuturesContracts from "features/futuresContracts/useFuturesContracts";
import useContractSubTypes from "features/contracts/useContractSubTypes";
import useOfferBuilder, { OfferField, OfferFormState } from "./useOfferBuilder";
import { useAddOfferReducers } from "./useOfferReducers";
import useMyProducer from "features/settings/producers/useMyProducer";
import useCommodityBalance from "features/offers/useCommodityBalance";
import useRealTimeSnapshot from "features/futuresContracts/useRealTimeSnapshot";
import { useFeesLoader } from "features/merchants/useFees";
import { listAllowedFieldsForOffer } from "features/merchants/customFieldHelpers";
import { listAllowedFeesForOffer } from "features/merchants/feeHelpers";
import useOfferModalJourney, { ModalJourney, shouldShowHedgeSettings } from "./useOfferModalJourney";
import useUiCustomization from "features/merchants/useUiCustomization";
import { useAllowedLocations } from "features/locations/useLocations";
import useHedgingSettings from "features/settings/hedging/useHedgingSettings";
import usePositionSettings from "features/positions/usePositionSettings";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import { filterOutDeliveryMethodsInvalidForPeriod, resolveDefaultDeliveryMethod, resolveDeliveryMethods } from "./deliveryMethodHelper";
import useContractExtendedTypes from "features/merchants/useContractExtendedTypes";
import useMappedLocations from "features/locations/mappings/useMappedLocations";

type AddOfferProps = {
    model: AddOfferModel;
    isPricingDelayedPriceContractModal?: boolean;
    isPricingWithBasisOffer?: boolean;
    isConvertBushelOnly?: boolean;
    onAdded?: (() => any);
    onClose(): void;
    roundCashPrice?: boolean;
    pricingBalances?: boolean;
    priceAcrossLocations?: boolean;
    lockDeliveryMethod?: boolean;
    contractTypes?: ContractType[];
};

const AddOfferModal: React.FC<AddOfferProps> = props => {

    const [isCreateNew, setIsCreateNew] = useState(false); // true if an offer was already submitted via "Submit + New"
    const [isSubmitting, showSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [workingQuantity, setWorkingQuantity] = useState(0);
    const [delayedPriceContracts, setDelayedPriceContracts] = useState<Contract[]>([]);

    const myProducer = useMyProducer();
    const deliveryMethods = useDeliveryMethods();
    const customFieldsLoader = useCustomFieldsLoader();
    const customFields = customFieldsLoader.data;
    const contractExtendedTypes = useContractExtendedTypes();
    const offeringsLoader = useOfferingsLoader();
    const offerings = offeringsLoader.data;
    const fcmAccounts = useFcmAccounts();
    const traders = useTraders();
    const marketSchedule = useMarketSchedule();
    const feesLoader = useFeesLoader();
    const fees = feesLoader.data;
    const allowedDeliveryMethodsLoader = useAllowedDeliveryMethodsLoader();
    const allowedDeliveryMethods = allowedDeliveryMethodsLoader.data;
    const uiCustomization = useUiCustomization();

    const canPushBasis = usePermission(Permissions.PushBasis);
    const canManualEntry = usePermission(Permissions.ManualEntry);
    const canPlaceHedges = usePermission(Permissions.PlaceLimitOrder);

    const activeMerchant = useActiveMerchant();
    const userConfiguration = useStoreSelector(m => m.userState.userConfiguration);
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);
    const myId = useStoreSelector(m => m.userState.internalId);
    const myTrader = traders.data.find(m => m.userId === myId);
    const canUseDetermineLater = myTrader?.locations.some(tl => tl.id === DetermineLaterLocation.id) ?? false;
    const myCommodities = useStoreSelector(m => m.myCommodities);
    const allowedLocations = useAllowedLocations();


    const displaySendToErpToggle = isFeatureEnabled(activeMerchant, MerchantFeature.SendToErpToggle);
    const basisOffersEnabled = isFeatureEnabled(activeMerchant, MerchantFeature.BasisOffers);

    const sourceSymbol = (props.model.futuresContract ?? getFuturesContract(props.model.deliveryPeriod, props.model.location))?.symbol;

    const defaultOffer = useMemo(() => buildOfferFromModel(), [isLoaded()]);
    const defaultState = useMemo(() => ({ lockDeliveryMethod: props.lockDeliveryMethod }), [props.lockDeliveryMethod]);

    const setBasisDeliveryPeriodsFilter = useMemo(() =>
        (allDeliveryPeriods: DeliveryPeriod[], forOffer: Offer, canPushBasisArg: boolean): DeliveryPeriod[] => {
            const base = filterDeliveryPeriods(allDeliveryPeriods, forOffer, canPushBasisArg);
            return applySetBasisSymbolFilter(base, forOffer, sourceSymbol);
        }, [sourceSymbol]);

    const reducers = useAddOfferReducers(props.roundCashPrice, defaultOffer, setBasisDeliveryPeriodsFilter, props.isPricingWithBasisOffer);
    const offerBuilder = useOfferBuilder(defaultOffer, reducers, defaultState, false);

    const offer = offerBuilder.offer;

    const offerType = offer?.type ?? props.model.offerType;
    const commodityId = offer?.commodity?.id ?? props.model.commodity?.id;

    const mappedLocations = useMappedLocations();

    const mappedLocationResult = useMemo(() => {
        return mappedLocations.getOptions(offer.location?.id, offer.inventoryLocation?.id);
    }, [offer.location, offer.inventoryLocation])

    const allowedCustomFields = activeMerchant.useCustomFields ? listAllowedFieldsForOffer(customFields, {
            ...offer,
            type: props.isPricingWithBasisOffer ? OfferTypeEnum.BasisOffer : offer.type,
        }) : [];

    const allowedFees = listAllowedFeesForOffer(fees, {
        ...offer,
        type: props.isPricingWithBasisOffer ? OfferTypeEnum.BasisOffer : offer.type,
    });
    const futuresContracts = useFuturesContracts(commodityId);
    const contractSubTypes = useContractSubTypes(toContractType(offerType),
        offer.customerWillSell,
        offer.deliveryPeriod?.cropYear.id,
        offer.paymentOption,
        offer.deliveryMethod);
    const deliveryPeriods = useDeliveryPeriods(commodityId);

    const dispatch = useDispatch();

    const timer: TimerResult = useTimer({ expiryTimestamp: new Date(), autoStart: false, onExpire: onTimerExpired });

    const realTimeSnapshot = useRealTimeSnapshot(offer.futuresContract?.id, shouldFetchSnapshot());

    const journey = useOfferModalJourney(offerBuilder, allowedCustomFields, handleSubmit);
    const hedgingSettings = useHedgingSettings();
    const positionSettings = usePositionSettings();

    useEffect(() => {
        if (defaultOffer == null) return;

        const isMarketOpen = marketSchedule.isMarketOpen(defaultOffer.commodity);

        if (isMarketOpen) {
            if (defaultOffer.trader?.priceLock != null
                && offerTypeHelper.isPriceLockAllowed(defaultOffer.type)
                && defaultOffer.orderType === OrderType.Market
                && isMarketOpen
                && !defaultOffer.commodity?.isCashOnly
            ) {
                onPriceLockChanged(true, defaultOffer.orderType);
            }
        }
        else if (defaultOffer.orderType === OrderType.Market) {
            offerBuilder.updateOffer(OfferField.PriceAtOpen, { isPriceAtOpenEnabled: activeMerchant.generalSettings.isPriceAtOpenEnabled });
        }

        if (defaultOffer.deliveryMethod === DeliveryMethod.DelayedPrice) {
            onDeliveryMethodChanged(OfferField.DeliveryMethod, defaultOffer);
        }

    }, [defaultOffer])

    useEffect(() => {
        if (!realTimeSnapshot.isLoaded) return;
        if (offer.type === OfferTypeEnum.BushelOnlyContract) return;
        if (props.isConvertBushelOnly) return;
        setCashPrice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realTimeSnapshot.data, realTimeSnapshot.isLoaded])

    const detailsValidator = useFormValidation();
    const customFieldsValidator = useFormValidation();
    const hedgeSettingsValidator = useFormValidation();

    const allowedPaymentOptions = listPaymentOptionsForOffer(offerType);

    const commodityBalance = useCommodityBalance(offer, () => shouldFetchCommodityBalance(offer));
    const maxAllowedQuantity = props.priceAcrossLocations
        ? props.model.remainingQuantity
        : commodityBalance.data?.quantity ?? offer.quantity;

    function onPriceLockChanged(isLocked: boolean, orderType: OrderType) {
        if (!isLocked) {
            if (orderType !== OrderType.Tas)
                realTimeSnapshot.refresh();

            if (timer.isRunning)
                timer.pause();
        }
        else
            timer.restart(getPriceLockTime());

        offerBuilder.updateOffer(null, {
            isPriceLocked: isLocked,
            snapshotPrice: isLocked ? realTimeSnapshot.data?.price : null,
            snapshotTime: isLocked ? realTimeSnapshot.data?.priceDateTime : null
        });
    }

    function getFuturesContract(deliveryPeriod?: DeliveryPeriod, location?: PhysicalLocation) {
        if (deliveryPeriod != null && location != null) {
            const bid = deliveryPeriod.bids?.find(x => x.location.id === location.id);
            return bid != null && bid.useSecondarySymbol ? deliveryPeriod.secondaryContract : deliveryPeriod.contract;
        }
        return deliveryPeriod?.contract;
    }

    function getDeliveryMethod(forOffer: Offer): DeliveryMethod {

        const deliveryMethods = listDeliveryMethods(forOffer);

        const dm = deliveryMethods.find(m => forOffer.deliveryMethod === m);

        if (dm != null) return dm;

        return getDefaultDeliveryMethod(forOffer);
    }

    function getDefaultDeliveryMethod(forOffer: Offer): DeliveryMethod {
        if (props.lockDeliveryMethod) {
            const deliveryMethods = listDeliveryMethods(forOffer);
            return deliveryMethods.find(m => props.model.deliveryMethod === m)
                ?? deliveryMethods.find(m => isCustomer && forOffer.trader?.defaultDeliveryMethod === m)
                ?? deliveryMethods[0];
        }

        return resolveDefaultDeliveryMethod(forOffer, activeMerchant, allowedDeliveryMethods, offerings, isCustomer);
    }

    function isPricingDelayedPriceContracts(forOffer: Offer) {
        return forOffer?.deliveryMethod === DeliveryMethod.DelayedPrice
            && forOffer?.producer?.id
            && offerTypeHelper.canHaveDelayedPriceContract(forOffer?.type)
    }

    function getDefaultOrderType(isSetBasis: boolean, isBasisOffer: boolean): OrderType {
        if (isSetBasis || isBasisOffer) return null;
        if (props.isConvertBushelOnly) return OrderType.ManualEntry;
        if (isCustomer) return OrderType.Limit;
        return myTrader?.orderType ?? OrderType.Limit;
    }

    function buildOfferFromModel(): Offer {
        const model = props.model;

        const isFixFutures = model.offerType === OfferTypeEnum.FixFuturesOnBasis;
        const isSetBasis = model.offerType === OfferTypeEnum.FixBasisOnHta;
        const isBushelOnly = model.offerType === OfferTypeEnum.BushelOnlyContract;
        const isBasisOffer = model.offerType === OfferTypeEnum.BasisOffer;
        const isPricing = isFixFutures || isSetBasis || props.isPricingDelayedPriceContractModal;

        const customerWillSell = getCustomerWillSell(model);
        const commodities = listCommodities(customerWillSell, model.offerType);

        const selectedCommodity = model.commodity ?? commodities[0];

        const producer = isCustomer ? myProducer : model.customer;
        if (isCustomer) {
            producer.accounts = producer.accounts?.filter(x => x.canManage);
        }
        const producerAccount = isCustomer && producer.accounts.length > 0 ? producer.accounts[0] : model.customerAccount;

        const futuresContract = model.futuresContract ?? getFuturesContract(model.deliveryPeriod, model.location);

        const defaultOffer: Partial<Offer> = {
            customerWillSell: customerWillSell,
            commodity: selectedCommodity,
            sourceContractId: isPricing || isBushelOnly ? model.contractId : null,
            displayId: model.displayId,
            type: model.offerType,
            orderType: getDefaultOrderType(isSetBasis, isBasisOffer),
            contractExtendedType: model.contractExtendedType,
            deliveryPeriod: model.deliveryPeriod,
            deliveryPeriodStartDate: model.deliveryPeriodStartDate ?? model.deliveryPeriod?.start,
            deliveryPeriodEndDate: model.deliveryPeriodEndDate ?? model.deliveryPeriod?.end,
            deferredPayDate: model.deferredPayDate,
            deliveryTerms: model.deliveryTerms,
            customFields: model.customFields ?? [],
            parentCustomFields: model.customFields ?? [],
            contractLocation: model.contractLocation ?? (activeMerchant.generalSettings.displayContractLocation && !activeMerchant.restrictLocationsOnContractsToMappings ? model.location : null),
            inventoryLocation: model.inventoryLocation ?? (activeMerchant.generalSettings.displayInventoryLocation && !activeMerchant.restrictLocationsOnContractsToMappings ? model.location : null),
            location: model.location,
            freightFee: isPricing ? model.freightFee : null,
            paymentOption: activeMerchant.generalSettings.displayPaymentOption ? (isPricing ? model.paymentOption : PaymentOption.Check) : null,
            notifyCustomer: activeMerchant.generalSettings.sendNotificationToCustomerDefault,
            isBasisFixed: model.isBasisFixed ?? false,
            deliveryMethod: props.isPricingDelayedPriceContractModal ? DeliveryMethod.DelayedPrice : model.deliveryMethod,
            parentFees: model.fees ?? [],
            fees: model.fees ?? [],
            currency: getCurrency(model.offerType, selectedCommodity),
            futuresContract: futuresContract,
            producer: producer,
            trader: myTrader,
            customerAccount: producerAccount

        }
        if (!isLoaded()) return { ...defaultOffer } as Offer;

        let futuresPrice, basis, cashPrice;

        if (selectedCommodity.isCashOnly) {
            futuresPrice = null;
            basis = null,
                cashPrice = model.price ?? 0;
        }
        else {
            futuresPrice = getInitialFuturesPrice(model, isSetBasis, futuresContract);
            basis = model.basis ?? model.deliveryPeriod?.bids?.find(m => m.location.id === model.location?.id)?.basis;
            cashPrice = model.price ?? NP.plus(futuresPrice ?? 0, basis ?? 0);
        }

        const expirationDate = getExpirationDate(model, futuresContract);
        let newOffer = {
            ...defaultOffer,
            futuresPrice: futuresPrice,
            cashPrice: cashPrice,
            basis: basis,
            disableErpSync: !displaySendToErpToggle,
            expirationDate: expirationDate,
            finalPricingDate: expirationDate
        } as Offer;

        newOffer.deliveryMethod = getDeliveryMethod(newOffer);

        if (newOffer.deliveryTerms != null) {
            newOffer.deliveryTerms = deliveryMethods.find(dmd => dmd.deliveryMethod === newOffer.deliveryMethod)?.defaultDeliveryTerms;
        }

        const locations = listLocationsForOffer(newOffer);
        if (newOffer.location == null && (!canUseDetermineLater)) {
            newOffer.location = locations[0];
        }
        else {
            // to fill the complete object with tags
            newOffer.location = locations.find(l => l.id === newOffer.location?.id);
        }

        // Adding default buy location to contract and inv location
        if (!newOffer.customerWillSell && newOffer.sourceContractId == null) {
            const defaultBuyLocation = locations.find(l => l.isDefaultBuyLocation);
            if (defaultBuyLocation != null) {
                newOffer.contractLocation = newOffer.inventoryLocation = newOffer.location = defaultBuyLocation;
            }
        }

        if (activeMerchant.generalSettings.displayRegion) {
            if (newOffer.location?.homeLocation == null) {
                newOffer.region = newOffer.location == null ?
                    locations.find(l => l.homeLocation == null) :
                    newOffer.location;
            }
            else {
                newOffer.region = {
                    ...defaultPhysicalLocation,
                    id: newOffer.location.homeLocation.id,
                    name: newOffer.location.homeLocation.name
                }
            }
        }

        if (isFixFutures || isSetBasis) {
            populateWorkingQuantity(newOffer.sourceContractId);
        }
        else if (props.isPricingDelayedPriceContractModal) {
            newOffer = { ...newOffer, ...assignDelayedPriceContract(props.model.delayedPriceContract) };
            populateWorkingQuantity(props.model.delayedPriceContract?.internalId);
        }
        else if (props.isConvertBushelOnly) {
            newOffer = { ...newOffer, ...assignBushelOnlyContract(props.model.bushelOnlyContract) };
            populateWorkingQuantity(props.model.bushelOnlyContract?.internalId);
        }

        newOffer.customFields = listAllowedFieldsForOffer(customFields, newOffer)
                        .filter(cf => cf.defaultValue)
                        .map(cf => ({ id: cf.id, name: cf.name, value: cf.defaultValue }))
                        
        return newOffer;
    }

    async function populateWorkingQuantity(contractId: number): Promise<any> {
        if (contractId == null) return;

        const result = await contractService.getWorkingQuantities(contractId);
        setWorkingQuantity(result);
        offerBuilder.updateOffer(OfferField.Quantity, { quantity: NP.minus(props.model.quantity, result) });
    }

    function getCurrency(offerType: OfferTypeEnum, offerCommodity: Commodity) {
        const commodity = myCommodities.find(x => x.id === offerCommodity?.id);
        return (commodity ? (offerTypeHelper.isHta(offerType) ? commodity.baseCurrency : commodity.localCurrency) : null);
    }


    async function getDelayedPriceContractsForCustomer(): Promise<Contract[]> {
        // TODO: check what happens with Customer Login
        const filter: ListContractsFilter = {
            contractType: ContractType.DelayedPrice,
            displayContracts: DisplayContractsEnum.withNonZeroQuantities,
            customerId: offer.producer.id,
            commodities: [offer.commodity]
        };
        const delayedPriceContracts = await contractService.listContracts(filter);
        return delayedPriceContracts.results;
    }

    function isLoaded(): boolean {
        return props.model != null &&
            offeringsLoader.isLoaded &&
            traders.isLoaded &&
            allowedDeliveryMethodsLoader.isLoaded &&
            userConfiguration != null &&
            feesLoader.isLoaded &&
            customFieldsLoader.isLoaded;
    }

    function goBack() {
        setErrors([]);
        if (journey.previousStep === ModalJourney.Details) {
            onWillGoToDetailsStep();
        }
        journey.goBack();
    }

    function goForward() {
        let isValid = true;
        switch (journey.currentStep) {
            case ModalJourney.Details:
                isValid = detailsValidator.current.validate();
                timer.pause();
                break;
            case ModalJourney.CustomFields:
                isValid = customFieldsValidator.current.validate();
                break;
            case ModalJourney.HedgeSettings:
                isValid = hedgeSettingsValidator.current.validate();
                break;
        }

        if (isValid) journey.goForward();
    }

    function onWillGoToDetailsStep() {
        if (offer.isPriceLocked) timer.restart(getPriceLockTime());
        //Reset the default FCM account
        offerBuilder.updateOffer(OfferField.PlaceHedge, { fcmAccount: null })
    }

    function getPriceLockTime(): Date {
        const priceLockTime = moment(offer.trader?.priceLock, 'HH:mm:ss');
        const expiryTimestamp = moment().add(priceLockTime.minute(), 'minutes').add(priceLockTime.seconds(), 'seconds');
        return expiryTimestamp.toDate();
    }

    function onTimerExpired() {
        onPriceLockChanged(false, offer.orderType);
    }

    function getContract(contractType: ContractType): Contract {
        const contract: Contract = {} as Contract;
        contract.cashPrice = null;
        contract.comments = offer.comments;
        contract.commodity = offer.commodity;
        contract.contractType = contractType;
        contract.contractExtendedType = offer.contractExtendedType;
        contract.contractSubType = offer.contractSubType;
        contract.createdBy = offer.createdBy;
        contract.customerWillSell = offer.customerWillSell;
        contract.deliveryMethod = offer.deliveryMethod;
        contract.commodity = offer.commodity;
        contract.deliveryPeriod = contractType === ContractType.BushelOnly ? null : offer.deliveryPeriod;
        contract.deliveryPeriodStartDate = offer.deliveryPeriodStartDate;
        contract.deliveryPeriodEndDate = offer.deliveryPeriodEndDate;
        contract.deliveryTerms = offer.deliveryTerms;
        contract.customerContractId = offer.customerContractId;
        contract.erpContractId = offer.erpContractId;
        contract.currency = offer.currency?.id;
        contract.finalPricingDate = offer.finalPricingDate;
        contract.futuresContract = offer.futuresContract;
        contract.location = offer.location;
        contract.inventoryLocation = offer.inventoryLocation;
        contract.contractLocation = offer.contractLocation;
        contract.region = offer.region;
        contract.paymentOption = offer.paymentOption;
        contract.deferredPayDate = offer.deferredPayDate;
        contract.interestStartDate = offer.interestStartDate;
        contract.interestRate = offer.interestRate;
        contract.freightFee = offer.freightFee;
        contract.premium = offer.deliveryPeriod?.premium;
        contract.producer = offer.producer;
        contract.customerAccount = offer.customerAccount;
        contract.quantityContracted = offer.quantity;
        contract.trader = offer.trader;
        contract.basis = offer.basis;
        contract.isBasisLocked = offer.isBasisFixed;
        contract.sourceContractId = offer.sourceContractId;
        contract.fees = offer.fees;
        contract.customFields = offer.customFields;
        contract.disableErpSync = offer.disableErpSync;
        contract.contractCurrency = offer.currency;
        contract.futuresPrice = offer.futuresPrice;
        return contract;
    }

    async function handleSubmit({ createNewAfterSubmitting = false } = {}) {
        showSubmitting(true);
        try {
            if (offerTypeHelper.originatesFromOffer(offer.type)) {
                const newOffer: Offer = { ...offer };
                if (props.isPricingWithBasisOffer) {
                    // when isPricingWithBasisOffer, the form should use all the behavior of FixBasisOnHta, so it has that type
                    // however, it should be submitted as type BasisOffer
                    newOffer.type = OfferTypeEnum.BasisOffer;
                }
                const placeHedge = (offerBuilder.formState.shouldShowHedgeOrderToggle || offer.orderType === OrderType.ManualEntry || offer.orderType === OrderType.Iceberg)
                    ? offerBuilder.formState.placeHedgeOrder
                    : null;
                if (props.priceAcrossLocations) {
                    await OfferActions.priceBalances(newOffer, placeHedge)(dispatch);
                }
                else {
                    await OfferActions.createOffer(newOffer, placeHedge)(dispatch);
                }
                props.onAdded && props.onAdded();
            }
            else {
                const contract = getContract(toContractType(offer.type));
                if (props.priceAcrossLocations) {
                    await ContractActions.priceBalances(contract)(dispatch);
                }
                else {
                    await ContractActions.createContract(contract)(dispatch);
                }
                props.onAdded && props.onAdded();
            }

            if (createNewAfterSubmitting) {
                showSubmitting(false);
                setIsCreateNew(true);
                onWillGoToDetailsStep();
                journey.goToStep(0);
            } else {
                props.onClose();
            }
        }
        catch (error) {
            showSubmitting(false);
            setErrors((error as ValidationError).errors ?? [localizeCommon("Notification_UnknownError")]);
        }
    }

    function submitAndCreateNew() {
        handleSubmit({ createNewAfterSubmitting: true });
    }

    function listOfferTypes(forOffer: Offer): OfferType[] {
        if (forOffer == null) return [];
        const bidViewType = forOffer?.commodity?.publicBidDisplay;

        // Define which offer types to exclude based on the bidViewType
        let excludedOfferTypes: OfferTypeEnum[] = [];
        switch (bidViewType) {
            case BidViewTypesEnum.basisOnly:
                excludedOfferTypes = [OfferTypeEnum.Hta, OfferTypeEnum.HtaMinPrice, OfferTypeEnum.Cash, OfferTypeEnum.CashMinPrice];
                break;
            case BidViewTypesEnum.cashOnly:
                excludedOfferTypes = [OfferTypeEnum.Hta, OfferTypeEnum.HtaMinPrice, OfferTypeEnum.BasisContract, OfferTypeEnum.BasisOffer];
                break;
            case BidViewTypesEnum.futuresOnly:
                excludedOfferTypes = [OfferTypeEnum.BasisContract, OfferTypeEnum.BasisOffer, OfferTypeEnum.Cash, OfferTypeEnum.CashMinPrice];
                break;
            case BidViewTypesEnum.basisCash:
                // No exclusions if the view is Basis & Cash
                break;
        }

        let offerTypes: OfferTypeEnum[] = [];
        if (!isCustomer) {
            if (props.isPricingDelayedPriceContractModal) excludedOfferTypes.push(OfferTypeEnum.DelayedPriceContract, OfferTypeEnum.BushelOnlyContract);
            if (props.isConvertBushelOnly) excludedOfferTypes.push(OfferTypeEnum.DelayedPriceContract, OfferTypeEnum.BasisOffer);

            offerTypes = (props.contractTypes ?? userConfiguration.allowedContractTypes).map(m => fromContractType(m));

            // if has BasisContract, insert BasisOffer after
            // note: for customers, BasisOffer is added via offerings
            if (offerTypes.includes(OfferTypeEnum.BasisContract) && !offerTypes.includes(OfferTypeEnum.BasisOffer)) {
                offerTypes.splice(offerTypes.indexOf(OfferTypeEnum.BasisContract) + 1, 0, OfferTypeEnum.BasisOffer);
            }
        }
        else {
            if (forOffer.location) {
                excludedOfferTypes = [OfferTypeEnum.FixBasisOnHta, OfferTypeEnum.FixFuturesOnBasis];
                const offering = offerings.find(o => o.location?.id === forOffer.location.id && o.commodity.id === forOffer.commodity.id);
                offerTypes = [...offering.offerTypes];

                if (offering.contractTypes.includes(ContractType.Basis)) offerTypes.push(OfferTypeEnum.BasisContract);
                if (offering.contractTypes.includes(ContractType.DelayedPrice)) offerTypes.push(OfferTypeEnum.DelayedPriceContract);
            }
        }

        // if basis offers disabled, add them to the excluded types so they get filtered out
        if (!basisOffersEnabled) excludedOfferTypes.push(OfferTypeEnum.BasisOffer);

        return offerTypes.filter(m => !excludedOfferTypes.includes(m))
            .map(m => ({ id: m, name: getOfferTypeName(m) }));
    }

    function listCustomerWill(location: PhysicalLocation, commodity: Commodity) {
        if (!isCustomer) {
            if (userConfiguration.canBuy && userConfiguration.canSell)
                return [true, false];
            else
                return [!userConfiguration.canBuy];
        }

        const offering = offerings
            .find(o => o.location?.id === location?.id && o.commodity.id === commodity.id);

        const customerWillList: boolean[] = [];

        if (offering?.allowSelling) customerWillList.push(offering.allowSelling);
        if (offering?.allowBuying) customerWillList.push(!offering.allowBuying);

        return customerWillList;
    }

    function isMatch(customerWillSell: boolean, offerType: OfferTypeEnum, offering: Offering, commodity?: Commodity, location?: PhysicalLocation): boolean {

        if ((customerWillSell && !offering.allowSelling) || (!customerWillSell && !offering.allowBuying))
            return false;

        if (!offering.offerTypes.includes(offerType) && !offering.contractTypes.includes(toContractType(offerType)))
            return false;

        if (offering.deliveryMethods.length === 0)
            return false;

        if (location != null && offering.location.id !== location.id) return false;

        if (commodity != null && offering.commodity.id !== commodity.id) return false;

        return true;
    }

    function listCommodities(customerWillSell: boolean, offerType: OfferTypeEnum): Commodity[] {

        if (props.model.commodity != null && !isCreateNew) {
            //Lock down commodity (but allow changing subsequent offers from "Submit + New")
            const options = isCustomer ? offerings.map(m => m.commodity) : myCommodities;
            const commodity = options.find(x => x.id === props.model.commodity.id);
            return commodity == null ? [] : [commodity];
        }

        if (!isCustomer) {
            return myCommodities;
        }

        return _.uniqBy(offerings
            .filter(o => isMatch(customerWillSell, offerType, o))
            .map(o => o.commodity),
            c => c.id);
    }

    function listFuturesContracts(forOffer: Offer): FuturesContract[] {
        if (forOffer == null || forOffer.commodity == null || listDeliveryPeriods(forOffer).length === 0 || forOffer.commodity.isCashOnly)
            return [];

        if (isCustomer)
            return [forOffer.deliveryPeriod?.contract];

        switch (forOffer.type) {
            case OfferTypeEnum.Cash:
            case OfferTypeEnum.CashMinPrice:
            case OfferTypeEnum.BasisContract:
            case OfferTypeEnum.BasisOffer:
            case OfferTypeEnum.FixBasisOnHta:
                if (canPushBasis)
                    return _.uniqBy(futuresContracts.data, f => f.id);
                break;

            case OfferTypeEnum.Hta:
            case OfferTypeEnum.HtaMinPrice:
            case OfferTypeEnum.FixFuturesOnBasis:
            case OfferTypeEnum.BushelOnlyContract:
                return _.uniqBy(futuresContracts.data, f => f.id);
        }

        return [forOffer.deliveryPeriod?.contract];
    }



    function listLocationsForOffer(forOffer: Offer, periodsForFilter?: DeliveryPeriod[]): PhysicalLocation[] {
        if (forOffer == null) return [];

        let locationList = allowedLocations;
        if (!isCustomer) {
            locationList = locationList.filter(m => m.commodities.some(c => c.id === forOffer.commodity?.id));
        }
        else {
            locationList = _.uniqBy(offerings
                .filter(o => isMatch(forOffer.customerWillSell, forOffer.type, o, forOffer.commodity))
                .map(o => o.location),
                l => l.id);

            // This is to bring the full location object, as the one coming from offerings only has id and name
            locationList = allowedLocations.filter(l => locationList.some(ol => ol.id === l.id));
        }

        // Only narrow by symbol when the caller supplies delivery periods
        if (periodsForFilter && forOffer.type === OfferTypeEnum.FixBasisOnHta && sourceSymbol != null) {
            // Once a delivery period is selected, restrict locations to that DP - otherwise locations from other matching DPs leak in.
            const periodsToCheck = forOffer.deliveryPeriod != null
                ? [periodsForFilter.find(dp => dp.id === forOffer.deliveryPeriod.id) ?? forOffer.deliveryPeriod]
                : periodsForFilter;
            const allowedLocationIds = new Set<string>();
            for (const dp of periodsToCheck) {
                for (const bid of dp.bids ?? []) {
                    if (bid.location?.id == null) continue;
                    if (locationBidResolvesToSymbol(bid.location.id, dp, sourceSymbol)) {
                        allowedLocationIds.add(bid.location.id);
                    }
                }
            }
            if (forOffer.location?.id != null) allowedLocationIds.add(forOffer.location.id);
            locationList = locationList.filter(l => allowedLocationIds.has(l.id));
        }

        return _.orderBy(locationList, l => l.name);
    }

    function listDeliveryMethods(forOffer: Offer): DeliveryMethod[] {
        const dms = resolveDeliveryMethods(forOffer, allowedDeliveryMethods, offerings, isCustomer);
        return filterOutDeliveryMethodsInvalidForPeriod(dms, forOffer.deliveryMethod, forOffer.deliveryPeriod, activeMerchant)
    }

    function listDeliveryPeriods(forOffer: Offer): DeliveryPeriod[] {
        return setBasisDeliveryPeriodsFilter(deliveryPeriods, forOffer, canPushBasis);
    }

    function shouldFetchCommodityBalance(forOffer: Offer) {

        if (props.priceAcrossLocations) return false;

        let location = forOffer.location;
        if (!isCustomer && activeMerchant.generalSettings.displayInventoryLocation) {
            location = forOffer.inventoryLocation;
        }

        const commodityBalanceType = getCommodityBalanceType(forOffer.deliveryMethod);
        if (location == null || forOffer.commodity == null || forOffer.producer == null ||
            commodityBalanceType == null || forOffer.customerAccount == null) return false;

        if (props.isPricingDelayedPriceContractModal && isPricingDelayedPriceContracts(forOffer))
            return false;

        if ([OfferTypeEnum.FixFuturesOnBasis, OfferTypeEnum.FixBasisOnHta].includes(forOffer?.type))
            return false;

        if (forOffer.deliveryMethod === DeliveryMethod.DelayedPrice && forOffer.delayedContract != null && forOffer.sourceContractId != null)
            return false;

        return true;
    }

    function setCashPrice() {
        if (!offer.isBasisFixed) {
            offer.basis = getBasis(offer.deliveryPeriod, offer.location, offer.type);
        }

        const snapshot = realTimeSnapshot.data;
        let cashPrice = NP.plus(snapshot?.price ?? 0, offer.basis ?? 0);

        // Added Rounding logic
        if (shouldRoundCashPrice(offer)) {
            cashPrice = getRoundedCashPrice(offer, cashPrice);
        }

        const updatedOffer = {
            futuresPrice: snapshot?.price,
            cashPrice: cashPrice,
            snapshotPrice: snapshot?.price,
            snapshotTime: snapshot?.priceDateTime
        };

        offerBuilder.updateOffer(null, updatedOffer);
    }

    function shouldRoundCashPrice(offer: Offer) {
        return props.roundCashPrice && offerTypeHelper.isCash(offer.type) && offer.deliveryPeriod != null
            && ![OrderType.Tas, OrderType.ManualEntry].includes(offer.orderType);
    }

    function getRoundedCashPrice(offer: Offer, cashPrice: number) {
        return roundCents(cashPrice, (offer.futuresContract?.isSettled ?
            offer.deliveryPeriod.commodity.roundingOnClose :
            offer.deliveryPeriod.commodity.roundingInSession));
    }

    function shouldFetchSnapshot(): boolean {
        if (!isLoaded()) return false;
        if (offer.futuresContract == null) return false;
        if (offer.orderType == null || offer.orderType === OrderType.Tas) return false;
        if (!offerTypeHelper.hasFuturesPrice(offer.type)) return false;

        return offer.orderType === OrderType.Market || !offerBuilder.formState.hasUserUpdatedCashPrice;
    }

    function getTitle(): string {
        switch (journey.currentStep) {
            case ModalJourney.Details:
                if (offer?.type === OfferTypeEnum.FixFuturesOnBasis) return localize("AddOfferModal_Header_SetFutures");
                else if (offer?.type === OfferTypeEnum.FixBasisOnHta && props.isPricingWithBasisOffer) return localize("AddOfferModal_Header_AddBasisOffer");
                else if (offer?.type === OfferTypeEnum.FixBasisOnHta) return localize("AddOfferModal_Header_SetBasis");
                if (props.isConvertBushelOnly) return localize("AddOfferModal_Header_ConvertContract");
                return localize("AddOfferModal_Header_AddOfferContract");
            case ModalJourney.CustomFields:
                return localize("AddOfferModal_Header_CustomFieldSelections");
            case ModalJourney.HedgeSettings:
                return localize("AddOfferModal_Header_HedgeSelections");
            case ModalJourney.Confirmation:
                if (offer?.type === OfferTypeEnum.FixFuturesOnBasis) return localize("AddOfferModal_Header_ReviewFutures");
                else if (offer?.type === OfferTypeEnum.FixBasisOnHta && props.isPricingWithBasisOffer) return localize("AddOfferModal_Header_ReviewBasisOffer");
                else if (offer?.type === OfferTypeEnum.FixBasisOnHta) return localize("AddOfferModal_Header_ReviewBasis");

                return localize('AddOfferModal_Header_ReviewOfferContract');
        }
    }

    function getBasis(deliveryPeriod: DeliveryPeriod, location: PhysicalLocation, offerType: OfferTypeEnum): number {
        if (deliveryPeriod?.commodity?.isCashOnly) {
            return null;
        }
        if (offerType === OfferTypeEnum.BushelOnlyContract)
            return null;
        if (offerType === OfferTypeEnum.FixFuturesOnBasis) {
            return props.model.basis;
        }
        else if (location == null || offerTypeHelper.isHta(offerType)) {
            return null;
        }
        else {
            const bids = deliveryPeriod?.bids?.find(x => x.location?.id === location?.id);
            return bids?.basis;
        }
    }

    async function onDeliveryMethodChanged(changedField: OfferField, newOffer: Offer, form?: OfferFormState): Promise<any> {
        if (!props.isPricingDelayedPriceContractModal && isPricingDelayedPriceContracts(newOffer)) {
            const dpContracts = await getDelayedPriceContractsForCustomer();
            if (props.pricingBalances && activeMerchant.enablePriceFromBalance) {
                const priceFromBalanceContract: Contract = { displayId: "Price from Balance", internalId: null, remainingQuantity: props.model.remainingQuantity } as Contract;
                dpContracts.unshift(priceFromBalanceContract);
            }
            setDelayedPriceContracts(dpContracts);
            if (dpContracts.length > 0) {
                newOffer = { ...newOffer, ...assignDelayedPriceContract(dpContracts[0]) };
                populateWorkingQuantity(dpContracts[0]?.internalId);
            }
            else {
                newOffer = { ...newOffer, delayedContract: null, sourceContractId: null };
            }
        }
        else {
            setDelayedPriceContracts([]);
            newOffer = { ...newOffer, delayedContract: null };
        }
        offerBuilder.updateOffer(changedField, newOffer, form);
    }

    async function onOfferUpdated(changedField: OfferField, partial: Partial<Offer>, form?: OfferFormState): Promise<any> {
        const newOffer = { ...offer, ...partial };

        switch (changedField) {
            case OfferField.Commodity:
                offerBuilder.updateOffer(changedField, newOffer, form);
                // turn price lock off if new commodity is cash only
                if (offer.isPriceLocked && newOffer.commodity.isCashOnly) {
                    onPriceLockChanged(false, newOffer.orderType);
                }
                break;
            case OfferField.DeliveryMethod:
                onDeliveryMethodChanged(changedField, newOffer, form);
                break;
            case OfferField.DPContract:
                offerBuilder.updateOffer(changedField, { ...newOffer, ...assignDelayedPriceContract(newOffer.delayedContract) }, form);
                populateWorkingQuantity(newOffer.delayedContract?.internalId);
                break;
            default:
                offerBuilder.updateOffer(changedField, newOffer, form);
        }
    }

    function assignDelayedPriceContract(contract: Contract): Partial<Offer> {
        return { delayedContract: contract, sourceContractId: contract?.internalId, quantity: contract?.remainingQuantity ?? props.model.remainingQuantity };
    }

    function assignBushelOnlyContract(contract: Contract): Partial<Offer> {
        return { bushelOnlyContract: contract, sourceContractId: contract?.internalId, quantity: contract?.remainingQuantity ?? props.model.remainingQuantity };
    }

    function getInitialFuturesPrice(model: AddOfferModel, isSetBasis: boolean, futuresContract: FuturesContract): number {
        if (isSetBasis) return model.futuresPrice;
        if (props.isConvertBushelOnly) return model.futuresPrice ?? null;
        return futuresContract?.price;
    }

    function getCustomerWillSell(model: AddOfferModel): boolean {
        if ([OfferTypeEnum.FixFuturesOnBasis, OfferTypeEnum.FixBasisOnHta].includes(model.offerType) || props.isPricingDelayedPriceContractModal) {
            return model.customerWillSell;
        }

        const customerWillSellList = listCustomerWill(model.location, model.commodity);

        if (isCustomer) {
            if (customerWillSellList.includes(myProducer.willSellDefaultValue)) {
                return myProducer.willSellDefaultValue;
            }
            else {
                return customerWillSellList.length > 0 ? customerWillSellList[0] : model.customerWillSell;
            }
        }
        // If user is trader and does not have permissions for both buy & sell
        else {
            if (!(userConfiguration.canBuy && userConfiguration.canSell)) {
                return !userConfiguration.canBuy
            }
        }

        return model.customerWillSell ?? true;
    }

    function listRegions() {
        const locations = listLocationsForOffer(offer, deliveryPeriods);

        const homeLocationIds = new Set(locations.map(m => m.homeLocation?.id));

        return locations.filter(m => homeLocationIds.has(m.id));
    }

    if (!isLoaded()) return <></>;

    return <Modal show onHide={props.onClose} backdrop="static">
        <Modal.Header closeButton>
            <Modal.Title>
                {getTitle()}
            </Modal.Title>
        </Modal.Header>

        <Modal.Body className="add-offer-modal">
            {journey.currentStep === ModalJourney.Details &&
                <FormValidation ref={detailsValidator}>
                    <AddOfferDetails
                        offer={offer}
                        formState={offerBuilder.formState}
                        canUseDetermineLater={canUseDetermineLater}
                        canPushBasis={canPushBasis && !props.priceAcrossLocations}
                        canManualEntry={canManualEntry}
                        canPlaceHedges={canPlaceHedges}
                        offerTypes={listOfferTypes(offer)}
                        customerWill={listCustomerWill(offer.location, offer.commodity)}
                        deliveryLocations={mappedLocationResult.deliveryLocations}
                        inventoryLocations={mappedLocationResult.inventoryLocations}
                        contractLocations={mappedLocationResult.contractLocations}
                        regions={listRegions()}
                        traders={traders.data}
                        delayedPriceContracts={delayedPriceContracts}
                        canChangeCommodity={props.model.commodity == null || isCreateNew}
                        isMarketOrder={offer.orderType === OrderType.Market}
                        isInitialLocationDetermineLater={props.model.location == null}
                        remainingContractQuantity={offer.delayedContract == null
                            ? NP.minus(props.model.quantity ?? 0, workingQuantity)
                            : offer.delayedContract.remainingQuantity}
                        setPriceLock={onPriceLockChanged}
                        commodities={listCommodities(offer.customerWillSell, offer.type)}
                        deliveryMethods={listDeliveryMethods(offer)}
                        deliveryPeriods={listDeliveryPeriods(offer)}
                        futuresContracts={listFuturesContracts(offer)}
                        timer={timer}
                        paymentOptions={allowedPaymentOptions}
                        userIsTrader={!isCustomer}
                        onOfferUpdated={onOfferUpdated}
                        doesCommodityBalanceExist={props.priceAcrossLocations || commodityBalance.data != null}
                        maxAllowedQuantity={maxAllowedQuantity}
                        isPricingDelayedPriceContractModal={props.isPricingDelayedPriceContractModal}
                        isPricingWithBasisOffer={props.isPricingWithBasisOffer}
                        isConvertBushelOnly={props.isConvertBushelOnly}
                        showPaymentOption={activeMerchant.generalSettings.displayPaymentOption}
                        realTimeFuturesPrice={realTimeSnapshot.data?.price}
                        allowPickingCustomer={props.model.customer == null}
                        fees={allowedFees}
                        priceAcrossLocations={props.priceAcrossLocations}
                        lockDeliveryMethod={props.lockDeliveryMethod}
                        roundCashPrice={props.roundCashPrice}
                        contractSubTypes={contractSubTypes}
                        contractExtendedTypes={contractExtendedTypes}
                        uiCustomization={uiCustomization} />
                </FormValidation>
            }
            {journey.currentStep === ModalJourney.CustomFields &&
                <FormValidation ref={customFieldsValidator}>
                    <AddCustomFieldModal
                        customFields={allowedCustomFields}
                        selectedCustomFields={offer?.customFields ?? []}
                        onCustomFieldsChanged={cf => offerBuilder.updateOffer(null, { customFields: cf })}
                    />
                </FormValidation>
            }
            {journey.currentStep === ModalJourney.HedgeSettings &&
                <FormValidation ref={hedgeSettingsValidator}>
                    <OfferHedgeSettings
                        offer={offer}
                        fcmAccounts={fcmAccounts}
                        onOfferUpdated={m => onOfferUpdated(null, m)}
                        isPlaceHedgeOrderChecked={offerBuilder.formState.placeHedgeOrder}
                        setPlaceHedgeOrderChecked={checked => offerBuilder.updateForm({ placeHedgeOrder: checked })}
                        hedgingSettings={hedgingSettings}
                        positionSettings={positionSettings} />
                </FormValidation>
            }
            {!isSubmitting && journey.currentStep === ModalJourney.Confirmation &&
                <OfferDetailsConfirmation
                    offer={offer}
                    isPricingWithBasisOffer={props.isPricingWithBasisOffer}
                    commodity={offer.deliveryPeriod?.commodity ?? offer.commodity}
                    errors={errors}
                    showHedgeSettings={shouldShowHedgeSettings(offer, offerBuilder.formState) && offerBuilder.formState.placeHedgeOrder}
                    uiCustomization={uiCustomization} />
            }
            {isSubmitting &&
                <Spinner animation="border" />
            }
        </Modal.Body>
        <Modal.Footer>
            {journey.currentStep !== ModalJourney.Details &&
                <Button variant="secondary" onClick={goBack} disabled={isSubmitting}
                    className={journey.currentStep === ModalJourney.Confirmation ? "mr-auto" : null}>
                    {localizeCommon("Button_Back")}
                </Button>
            }
            {[ModalJourney.Details, ModalJourney.Confirmation].includes(journey.currentStep) &&
                <Button variant="secondary" onClick={props.onClose} disabled={isSubmitting}>
                    {localizeCommon("Button_Cancel")}
                </Button>
            }
            {journey.currentStep === ModalJourney.Confirmation &&
                offer?.type !== OfferTypeEnum.FixBasisOnHta &&
                offer?.type !== OfferTypeEnum.FixFuturesOnBasis &&
                !props.isConvertBushelOnly &&
                <Button variant="primary" onClick={submitAndCreateNew} disabled={isSubmitting}>
                    {localizeCommon("Button_SubmitAndNew")}
                </Button>
            }
            <Button variant="primary" onClick={goForward} disabled={isSubmitting}>
                {localizeCommon(journey.currentStep === ModalJourney.Confirmation
                    ? "Button_Submit"
                    : "Button_Next")}
            </Button>
        </Modal.Footer>
    </Modal>;
}

function getExpirationDate(model: AddOfferModel, futuresContract: FuturesContract): Date {
    if (model.deliveryPeriod?.commodity?.isCashOnly) {
        return model.deliveryPeriod.end;
    }
    else if (offerTypeHelper.isBasis(model.offerType)) {
        return getSecondLastBusinessDayOfLastMonth(model.futuresContract?.expiration)
    }
    else if (model.expirationDate) {
        return model.expirationDate;
    }
    else {
        const expiration = futuresContract?.expiration;
        return expiration == null ? null : getPreviousDayForGivenDate(expiration);
    }
}

function locationBidResolvesToSymbol(locationId: string, dp: DeliveryPeriod, symbol: string): boolean {
    return resolveDeliveryPeriodFuturesContract(locationId, dp)?.symbol === symbol;
}

function applySetBasisSymbolFilter(filtered: DeliveryPeriod[], forOffer: Offer, sourceSymbol: string): DeliveryPeriod[] {
    if (forOffer?.type !== OfferTypeEnum.FixBasisOnHta || sourceSymbol == null) return filtered;

    let result = filtered.filter(dp => {
        if (forOffer.location == null) {
            return dp.bids?.some(b => b.location?.id != null && locationBidResolvesToSymbol(b.location.id, dp, sourceSymbol)) ?? false;
        }
        return locationBidResolvesToSymbol(forOffer.location.id, dp, sourceSymbol);
    });

    const currentDp = forOffer.deliveryPeriod;
    if (currentDp?.id != null && !result.some(dp => dp.id === currentDp.id)) {
        result = [...result, currentDp];
    }

    return result;
}

function filterDeliveryPeriods(deliveryPeriods: DeliveryPeriod[], forOffer: Offer, canPushBasis: boolean): DeliveryPeriod[] {
    if (forOffer == null) return [];

    let filteredDeliveryPeriods = deliveryPeriods;

    if (!canPushBasis) //AUT-3527: Traders with Push Basis can choose any delivery period, even if it doesn't have an associated bid
    {
        if (forOffer.location != null && [OfferTypeEnum.Cash, OfferTypeEnum.CashMinPrice, OfferTypeEnum.BasisContract, OfferTypeEnum.BasisOffer].includes(forOffer.type)) {
            filteredDeliveryPeriods = filteredDeliveryPeriods.filter(dp => dp.bids.some(b => (b.location.id === forOffer.location?.id || forOffer.location == null) && b.cash != null));//TODO: need to handle bids that don't have cash values
        }

        if (forOffer?.deliveryPeriod != null && !filteredDeliveryPeriods?.some(x => x.id === forOffer?.deliveryPeriod?.id) && [OfferTypeEnum.FixBasisOnHta, OfferTypeEnum.FixFuturesOnBasis].includes(forOffer.type)) {
            filteredDeliveryPeriods = [...filteredDeliveryPeriods, forOffer.deliveryPeriod];
        }
    }

    return filteredDeliveryPeriods;
}

export default AddOfferModal;